import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useMapSelector} from '../../../../hooks/map-selector';
import {DoorModel} from '../../../../types/door';
import {SelectItem} from '../../../../components/Select';
import {DoorBrandModel} from '../../../../types/door-brand';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {isValidDoorNumber} from '../../../../utils/validation';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';

const BACK_URL = '/settings/door';

export const DoorEditScreen = memo(function DoorEditScreen() {
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const doorList = useSelector(state => state.control.doorList);
    const doorBranchOptionList = useMapSelector(
        state => state.control.doorBrandList,
        doorBrand => ({value: doorBrand, label: doorBrand.name}),
    ) || [];

    const [selected, setSelected] = useState<DoorModel>(null);
    const [name, setName] = useState('');
    const [doorBrandOption, setDoorBrandOption] = useState<SelectItem<DoorBrandModel>>(null);
    const [doorPath, setDoorPath] = useState('');
    const [doorNumber, setDoorNumber] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen geçerli bir kapı adı giriniz.');

            return false;
        }

        if (doorBrandOption == null) {
            failure('Lütfen geçerli bir kapı markası seçiniz.');

            return false;
        }

        if (doorPath == null || doorPath.length === 0) {
            failure('Lütfen geçerli bir URL giriniz.');

            return false;
        }

        if (!isValidDoorNumber(doorNumber)) {
            failure('Lütfen geçerli bir kapı numarası giriniz.');

            return false;
        }

        if (username == null || username.length === 0) {
            failure('Lütfen geçerli bir kullanıcı adı giriniz.');

            return false;
        }

        if (password == null || password.length === 0) {
            failure('Lütfen geçerli bir şifre giriniz.');

            return false;
        }

        return true;
    }, [doorBrandOption, doorPath, doorNumber, failure, name, password, username]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let doorPathWithScheme = doorPath;

        if (doorPathWithScheme.indexOf('http://') !== 0) {
            doorPathWithScheme = 'http://' + doorPath;
        }

        const doorModel: DoorModel = {
            id: selected?.id || null,
            name,
            door_brand_id: doorBrandOption.value.id,
            door_path: doorPathWithScheme,
            door_number: Number(doorNumber),
            username,
            password,
        };

        return rpc.updateDoor(doorModel)
            .then(() => {
                success('Kapı başarılı bir şekilde ' + (doorModel.id ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
    }, [
        doorBrandOption?.value?.id,
        doorPath,
        doorNumber,
        failure,
        isFieldsValid,
        name,
        onBackPressed,
        password,
        rpc,
        selected?.id,
        success,
        username,
    ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteDoor(selected.id)
            .then(() => {
                success('Kapı başarılı bir şekilde silindi.');
                onBackPressed();
            })
            .catch(failure);
    }, [failure, onBackPressed, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type='door'
                    questionText='Silmek istediğinizden emin misiniz?'
                    questionTextRelational={message => `${selected?.name} ile birlikte ${message} silinecektir. Devam edilsin mi?`}
                    onPress={onDeletePressed}/>,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        }], [selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const door = doorList.find(doorItem => doorItem.id === params['id']);
        if (door == null) {
            return;
        }

        setSelected(door);
        setName(door.name);
        setDoorBrandOption(
            doorBranchOptionList.find(doorBranchOption => doorBranchOption.value.id === door.door_brand_id) ||
            doorBranchOptionList[0] ||
            null);
        setDoorPath(door.door_path);
        setDoorNumber(door.door_number.toString());
        setUsername(door.username);
        setPassword(door.password);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setDoorBrandOption(doorBranchOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kapı adı yazınız...'
                            label='Kapı Adı'
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label='Marka / Model'
                            options={doorBranchOptionList}
                            onChange={setDoorBrandOption}
                            value={doorBrandOption}/>
                        <SettingInputComponent
                            placeholder='Röle numarası yazınız...'
                            label='Röle Numarası'
                            value={doorNumber}
                            setValue={setDoorNumber}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            maxLength={200}
                            placeholder='URL yazınız...'
                            label='URL (IP)'
                            value={doorPath}
                            setValue={setDoorPath}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder='Kullanıcı adı yazınız...'
                            label='Kullanıcı adı'
                            value={username}
                            setValue={setUsername}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingInputComponent
                            type='password'
                            placeholder='Şifre yazınız...'
                            label='Şifre'
                            value={password}
                            setValue={setPassword}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
